import {Component, OnInit} from '@angular/core';
import {NavigationEnd, Router} from '@angular/router';
import {MatSnackBar} from '@angular/material';
import {select, Store} from '@ngrx/store';

import * as fromRoot from '../../../reducers';
import * as fromAfbApi from '../../../afb-api/reducers';
import * as fromDashboard from '../../../dashboard/reducers';
import * as fromDeviceGrid from '../../reducers';

import * as group from '../../../core/store/actions/group.action';
import * as trigger from '../../../core/store/actions/trigger.action';
import {Observable, combineLatest} from 'rxjs';

import {filter, take, withLatestFrom} from 'rxjs/operators';
import {
  HoverDevice,
  HoverOut,
  SelectCondition,
  SelectGroupId,
  SelectFloorId,
  DeselectCondition,
  DeselectGroupId,
  DeselectFloorId
} from '../../actions/device-grid-view.actions';
import {Group} from '../../../shared';
import {ITriggerStore} from '../../../dashboard/shared';
import {OrganizationWithToken} from '../../../core/models/core';
import {SelfOrganizationUser} from '../../../afb-api/models/organization-user';
import {Snapshot} from '../../../shared/models/snapshot';
import {Device, DeviceWithPolledAirDatum, DeviceWithUuid} from '../../../afb-api/models/device';
import {GoogleAnalyticsService} from '../../../core';
import {ScoreToConditionPipe} from '../../../shared/pipes/score-to-condition.pipe';
import {AddSelectedDevice, EmptySelectedDevice, RemoveSelectedDevice} from '../../../dashboard/actions/dashboard-device.actions';
import {NuggetMode} from '../../enums/nugget-mode.enum';
import {OrganizationUserRole} from '../../../afb-api/enums/organization-user-role.enum';
import {skip, map} from 'rxjs/internal/operators';
import {Floor} from '../../../afb-api/models/floor';
import {Condition} from '../../../afb-api/enums/condition.enum';
import {ConditionWithStatus, FloorWithStatus, GroupWithStatus} from '../../models/device-grid';
import {of} from 'rxjs/internal/observable/of';

@Component({
  selector: 'awair-device-grid',
  templateUrl: './device-grid.component.html',
  styleUrls: ['./device-grid.component.scss']
})

export class DeviceGridComponent implements OnInit {

  selectedDeviceUuids: string[] = [];

  NuggetMode = NuggetMode;
  OrganizationUserRole = OrganizationUserRole;

  // Noti Settings
  isNotiSetting: boolean = false;
  triggerEntities: { [deviceUuid: string]: ITriggerStore };

  selectedOrganization$: Observable<OrganizationWithToken> = this.rootStore.pipe(
    select(fromRoot.selectedOrganization),
    filter((selectedOrganization: OrganizationWithToken) => selectedOrganization != null)
  );
  selfOrganizationUser$: Observable<SelfOrganizationUser> = this.afbApiStore.pipe(
    select(fromAfbApi.selfOrganizationUser),
    filter((selfOrganizationUser: SelfOrganizationUser) => selfOrganizationUser != null)
  );

  // Snapshots
  selectedDeviceSnapshots$: Observable<Snapshot[]> = this.dashboardStore.pipe(
    select(fromDashboard.selectedDeviceSnapshots),
    filter((snapshots: Snapshot[]) => snapshots != null)
  );
  hoveredDeviceSnapshot$: Observable<Snapshot> = this.deviceGridStore.pipe(
    select(fromDeviceGrid.getHoveredDeviceSnapshot)
  );

  // For device nuggets,
  devicesWithPolledAirData$: Observable<DeviceWithPolledAirDatum[]> = this.afbApiStore.pipe(
    select(fromAfbApi.getDevicesWithPolledAirData),
    filter((devicesWithPolledAirData: DeviceWithPolledAirDatum[]) => devicesWithPolledAirData != null)
  );
  hasEmptyGroupFilter$: Observable<boolean> = this.deviceGridStore.pipe(
    select(fromDeviceGrid.hasEmptyGroupFilter)
  );
  hasEmptyFloorFilter$: Observable<boolean> = this.deviceGridStore.pipe(
    select(fromDeviceGrid.hasEmptyFloorFilter)
  );
  hasEmptyConditionFilter$: Observable<boolean> = this.deviceGridStore.pipe(
    select(fromDeviceGrid.hasEmptyConditionFilter)
  );
  selectedGroupIds$: Observable<string[]> = this.deviceGridStore.pipe(
    select(fromDeviceGrid.selectedGroupIds)
  );
  selectedFloorIds$: Observable<string[]> = this.deviceGridStore.pipe(
    select(fromDeviceGrid.selectedFloorIds)
  );
  selectedConditions$: Observable<Condition[]> = this.deviceGridStore.pipe(
    select(fromDeviceGrid.selectedConditions)
  );
  checkedFilterCount$: Observable<string> = this.deviceGridStore.pipe(
    select(fromDeviceGrid.checkedFilterCount),
    map((checkedFilterCount: number) => {
      if (checkedFilterCount < 1) {
        return '';
      } else if (checkedFilterCount >= 99) {
        return '99+';
      } else {
        return checkedFilterCount.toString();
      }
    })
  );

  filteredDevicesWithPolledAirData$: Observable<DeviceWithPolledAirDatum[]> = combineLatest(
    this.devicesWithPolledAirData$,
    this.hasEmptyGroupFilter$,
    this.hasEmptyFloorFilter$,
    this.hasEmptyConditionFilter$,
    this.selectedGroupIds$,
    this.selectedFloorIds$,
    this.selectedConditions$,
    (
      devicesWithPolledAirData: DeviceWithPolledAirDatum[],
      hasEmptyGroupFilter: boolean,
      hasEmptyFloorFilter: boolean,
      hasEmptyConditionFilter: boolean,
      selectedGroupIds: string[],
      selectedFloorIds: string[],
      selectedConditions: Condition[]
    ) => {
      const filteredDevicesWithPolledAirData = devicesWithPolledAirData
        .filter((deviceWithPolledAirDatum: DeviceWithPolledAirDatum) =>
          this.filterDeviceWithPolledAirDatum(
            deviceWithPolledAirDatum,
            hasEmptyGroupFilter,
            hasEmptyFloorFilter,
            hasEmptyConditionFilter,
            selectedGroupIds,
            selectedFloorIds,
            selectedConditions
          )
        );

      return this.sortDevicesWithPolledAirData(filteredDevicesWithPolledAirData);
    }
  );

  isGetDevicesLoaded$: Observable<boolean> = this.afbApiStore.pipe(
    select(fromAfbApi.isGetDevicesLoaded),
    filter((isGetDevicesLoaded: boolean) => isGetDevicesLoaded),
    take(1)
  );

  devices$: Observable<Device[]> = this.afbApiStore.pipe(
    select(fromAfbApi.devices),
    filter((devices: Device[]) => devices != null)
  );

  selectedDeviceUuids$: Observable<string[]> = this.dashboardStore.pipe(
    select(fromDashboard.selectedDeviceUuids),
    filter((selectedDeviceUuids: string[]) => {
      if (selectedDeviceUuids != null) {
        this.selectedDeviceUuids = selectedDeviceUuids;
        return true;
      }
    })
  );

  groupList$: Observable<Group[]> = this.rootStore.pipe(
    select(fromRoot.getCoreGroupList)
  );

  groupListWithStatus$: Observable<GroupWithStatus[]> = combineLatest(
    this.groupList$,
    this.selectedGroupIds$,
    (groupList: Group[], selectedGroupIds: string[]) => {
      const groupListWithStatus: GroupWithStatus[] = groupList.reduce((acc, group) => {
        return [
          ...acc,
          {
            ...group,
            isChecked: selectedGroupIds.some((selectedGroupId) => selectedGroupId === group.id)
          }
        ];
      }, []);

      return groupListWithStatus;
    }
  );

  floorList$: Observable<Floor[]> = this.afbApiStore.pipe(
    select(fromAfbApi.floors)
  );

  floorListWithStatus$: Observable<FloorWithStatus[]> = combineLatest(
    this.floorList$,
    this.selectedFloorIds$,
    (floorList: Floor[], selectedFloorIds: string[]) => {
      const floorListWithStatus: FloorWithStatus[] = floorList.reduce((acc, floor) => {
        return [
          ...acc,
          {
            ...floor,
            isChecked: selectedFloorIds.some((selectedFloorId) => selectedFloorId === floor.id)
          }
        ];
      }, []);

      return floorListWithStatus;
    }
  );

  conditionList$: Observable<Condition[]> = of(
    [
      Condition.Good,
      Condition.Fair,
      Condition.Poor,
      Condition.Disconnected
    ]
  );

  conditionListWithStatus$: Observable<ConditionWithStatus[]> = combineLatest(
    this.conditionList$,
    this.selectedConditions$,
    (conditionList: Condition[], selectedConditions: Condition[]) => {
      const conditionListWithStatus: ConditionWithStatus[] = conditionList.reduce((acc, condition) => {
        return [
          ...acc,
          {
            condition,
            isChecked: selectedConditions.some((selectedCondition) => selectedCondition === condition)
          }
        ];
      }, []);

      return conditionListWithStatus;
    }
  );

  triggerEntities$: Observable<{ [deviceUuid: string]: ITriggerStore }> = this.rootStore.pipe(
    select(fromRoot.getTriggerEntities),
    skip(1)
  );

  constructor(private rootStore: Store<fromRoot.State>,
              private afbApiStore: Store<fromAfbApi.State>,
              private dashboardStore: Store<fromDashboard.State>,
              private deviceGridStore: Store<fromDeviceGrid.State>,
              private googleAnalyticsService: GoogleAnalyticsService,
              private scoreToConditionPipe: ScoreToConditionPipe,
              private router: Router,
              private snackBar: MatSnackBar) {
  }

  ngOnInit() {
    this.dashboardStore.dispatch(new EmptySelectedDevice());

    this.devices$.pipe(
      withLatestFrom(this.selectedOrganization$)
    ).subscribe(([devices, organization]: [Device[], OrganizationWithToken]) => {
      this.rootStore.dispatch(new trigger.RetrieveTriggerAction({orgToken: organization.token, deviceList: devices}));
    });

    this.triggerEntities$.subscribe((entities: { [deviceUuid: string]: any }) => {
      this.triggerEntities = {...entities};
    });

    this.router.events.subscribe((events) => {
      if (events instanceof NavigationEnd) {
        this.isNotiSetting = this.router.url.includes('dashboard/event');
      }
    });
  }

  // If passing with timestamp, that means selected by notification
  select(selectedDevice: DeviceWithUuid, selectedDeviceUuids: string[]): void {
    const selectedIndex: number = selectedDeviceUuids.indexOf(selectedDevice.uuid);

    if (selectedIndex === -1 && selectedDeviceUuids.length >= 8) {
      this.snackBar.open('Only 8 devices can be selected', undefined, {
        duration: 2000,
        panelClass: ['toast-alert']
      });
    } else if (selectedIndex === -1) {
      this.rootStore.dispatch(new AddSelectedDevice({
        deviceUuid: selectedDevice.uuid
      }));
    } else {
      this.rootStore.dispatch(new RemoveSelectedDevice({
        deviceUuid: selectedDevice.uuid
      }));
    }
  }

  hoverDevice(hoveredDevice: DeviceWithUuid): void {
    if (hoveredDevice != null) {
      this.deviceGridStore.dispatch(new HoverDevice({
        deviceUuid: hoveredDevice.uuid
      }));
    }
  }

  hoverOut(): void {
    this.deviceGridStore.dispatch(new HoverOut());
  }

  confirmFilterProgress(): boolean {
    if (this.selectedDeviceUuids.length > 0) {
      if (confirm('Applying filter will deselect all selected devices.\nDo you want to proceed?')) {
        this.dashboardStore.dispatch(new EmptySelectedDevice());
        return true;
      } else {
        return false;
      }
    } else {
      return true;
    }
  }

  selectCondition(selectedCondition: ConditionWithStatus): void {
    if (selectedCondition != null && this.confirmFilterProgress()) {
      this.googleAnalyticsService.emitEvent('Filter', 'click', (selectedCondition.condition));
      this.deviceGridStore.dispatch(new SelectCondition(<Condition>selectedCondition.condition));
    }
    // else {
    //   TODO: Check GA (No filter)
    //   this.googleAnalyticsService.emitEvent('Filter', 'click', 'No Filter');
    //   this.deviceGridStore.dispatch(new DeselectCondition());
    // }
  }

  selectGroup(selectedGroup: GroupWithStatus): void {
    if (selectedGroup != null && this.confirmFilterProgress()) {
      // this.selectedGroup = {...selectedGroup};
      // TODO: Check GA (Group)
      this.googleAnalyticsService.emitEvent('Group', 'select');

      this.deviceGridStore.dispatch(new SelectGroupId({
        groupId: selectedGroup.id.toString()
      }));
    }

    // TODO: Check Group devices avg
    // Remove all selectedDeviceList and also reflect to timeline
    // this.dashboardStore.dispatch(new EmptySelectedDevice());
    // Dispatch selected group to change dotted timeline
    // this.rootStore.dispatch(new group.SelectGroupAction(this.selectedGroup != null ? this.selectedGroup.id : null));
  }

  selectFloor(selectedFloor: FloorWithStatus): void {
    if (selectedFloor != null && this.confirmFilterProgress()) {
      // this.selectedFloor = {...selectedFloor};
      // TODO: Check GA (Floor)
      // this.googleAnalyticsService.emitEvent('Floor', 'select');

      this.deviceGridStore.dispatch(new SelectFloorId({
        floorId: selectedFloor.id.toString()
      }));
    }

    // TODO: Check Floor devices avg
    // Remove all selectedDeviceList and also reflect to timeline
    // this.dashboardStore.dispatch(new EmptySelectedDevice());
    // Dispatch selected group to change dotted timeline
    // this.rootStore.dispatch(new group.SelectGroupAction(this.selectedGroup != null ? this.selectedGroup.id : null));
  }

  private filterDeviceWithPolledAirDatum(
    deviceWithPolledDatum: DeviceWithPolledAirDatum,
    hasEmptyGroupFilter: boolean,
    hasEmptyFloorFilter: boolean,
    hasEmptyConditionFilter: boolean,
    selectedGroupIds: string[],
    selectedFloorIds: string[],
    selectedConditions: Condition[]
  ): boolean {
    const entityGroupId: string | null = (
      deviceWithPolledDatum &&
      deviceWithPolledDatum.device &&
      deviceWithPolledDatum.device.groupId) ?
      deviceWithPolledDatum.device.groupId : null;
    const entityFloorId: string | null = (
      deviceWithPolledDatum &&
      deviceWithPolledDatum.device &&
      deviceWithPolledDatum.device.floorId) ?
      deviceWithPolledDatum.device.floorId : null;
    const entityCondition: Condition | null = (
      deviceWithPolledDatum &&
      deviceWithPolledDatum.polledAirDatum &&
      deviceWithPolledDatum.polledAirDatum.score) ?
      this.scoreToConditionPipe.transform(deviceWithPolledDatum.polledAirDatum.score) : null;
    const entityIsOnline: boolean | null = (
      deviceWithPolledDatum &&
      deviceWithPolledDatum.polledAirDatum &&
      deviceWithPolledDatum.polledAirDatum.isOnline != null) ?
      deviceWithPolledDatum.polledAirDatum.isOnline : null;

    const groupIdResult: boolean = hasEmptyGroupFilter ? true
      : selectedGroupIds.some((groupId: string) => groupId === entityGroupId);
    const floorIdResult: boolean = hasEmptyFloorFilter ? true
      : selectedFloorIds.some((floorId: string) => floorId === entityFloorId);
    const conditionResult: boolean = hasEmptyConditionFilter ? true
      : selectedConditions.some((condition: Condition) => {
        if (condition === Condition.Disconnected) {
          return !entityIsOnline;
        } else {
          return (condition === entityCondition) && entityIsOnline;
        }
      });

    return groupIdResult && floorIdResult && conditionResult;
  }

  private sortDevicesWithPolledAirData(devicesWithPolledAirData: DeviceWithPolledAirDatum[]): DeviceWithPolledAirDatum[] {
    const nullElementList = [];
    const hasElementList = [];

    devicesWithPolledAirData.map((deviceWithPolledAirDatum: DeviceWithPolledAirDatum) => {
      deviceWithPolledAirDatum.device.deviceName == null ? nullElementList.push(deviceWithPolledAirDatum)
        : hasElementList.push(deviceWithPolledAirDatum);
    });

    hasElementList.sort((prev: DeviceWithPolledAirDatum, next: DeviceWithPolledAirDatum) => {
      if (typeof prev.device.deviceName === 'string') {
        // To not mutate original value
        const lowerCaseA = prev.device.deviceName != null ? prev.device.deviceName.toLowerCase() : null;
        const lowerCaseB = next.device.deviceName != null ? next.device.deviceName.toLowerCase() : null;

        if (lowerCaseA < lowerCaseB) {
          return -1;
        } else if (lowerCaseA > lowerCaseB) {
          return 1;
        } else {
          return 0;
        }
      } else {
        if (prev.device.deviceName < next.device.deviceName) {
          return -1;
        } else if (prev.device.deviceName > next.device.deviceName) {
          return 1;
        } else {
          return 0;
        }
      }
    });

    return hasElementList.concat(nullElementList);
  }
}
